import type { CoreEvent, PeriodStructure } from './types.js';

/**
 * Clock arithmetic.
 *
 * The game clock counts DOWN and is recorded as milliseconds remaining
 * (BUILD_PLAN 5.3). Every derived time in this engine is "elapsed game time",
 * measured from tipoff, computed from that reading.
 *
 * Deriving elapsed time from the game clock rather than from wall time is what
 * makes stopped-clock leagues work with no extra machinery: the game clock does
 * not advance during a dead ball, so time a player spends standing at the free
 * throw line never lands in anyone's minutes. It also means a page refresh, a
 * phone lock, and a browser crash cost nothing, because nothing is being
 * accumulated in memory (BUILD_PLAN 5.6).
 *
 * The cost, stated plainly in BUILD_PLAN 13.3: if the scorekeeper's clock drifts
 * from the referee's, minutes drift with it. That is mitigated by the SET_CLOCK
 * correction event and by reporting minutes to 10-second precision, not by
 * pretending the app clock is authoritative.
 */

/** Length of a given period. Periods past regulation use the overtime length. */
export function periodLengthMs(period: number, structure: PeriodStructure): number {
  return period <= structure.regulationPeriods
    ? structure.regulationLengthMs
    : structure.overtimeLengthMs;
}

/** Total game time elapsed at the moment the given period begins. */
export function elapsedBeforePeriodMs(period: number, structure: PeriodStructure): number {
  let total = 0;
  for (let p = 1; p < period; p += 1) {
    total += periodLengthMs(p, structure);
  }
  return total;
}

/**
 * Elapsed game time for a clock reading.
 *
 * A reading greater than the period length (which a SET_CLOCK correction can
 * legitimately produce if the scorekeeper types a value from a scoreboard that
 * had not started) clamps to the start of the period rather than producing
 * negative elapsed time. Negative time would silently corrupt every stint that
 * followed, so it is clamped here and reported as a warning by the caller.
 */
export function toElapsedMs(
  period: number,
  gameClockMs: number,
  structure: PeriodStructure,
): number {
  const length = periodLengthMs(period, structure);
  const withinPeriod = Math.min(Math.max(length - gameClockMs, 0), length);
  return elapsedBeforePeriodMs(period, structure) + withinPeriod;
}

/** Elapsed game time at which an event was recorded. */
export function eventElapsedMs(event: CoreEvent, structure: PeriodStructure): number {
  return toElapsedMs(event.period, event.gameClockMs, structure);
}

/**
 * Round a duration to the nearest 10 seconds.
 *
 * BUILD_PLAN 5.6 and 13.3: report minutes at the precision the clock actually
 * supports. Showing a parent "14:23.417" claims an accuracy the scorekeeper's
 * thumb does not have, and the false precision is what makes a small, honest
 * error look like a bug.
 */
export function roundToTenSecondsMs(durationMs: number): number {
  return Math.round(durationMs / 10_000) * 10_000;
}

/** Format a duration as m:ss for display. Never negative. */
export function formatDuration(durationMs: number): string {
  const safe = Math.max(durationMs, 0);
  const totalSeconds = Math.round(safe / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes)}:${String(seconds).padStart(2, '0')}`;
}
